import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  arrayRemove,
  arrayUnion,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  updateDoc,
  where,
} from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, db } from '../../firebase';
import '../../styles/ManageRoster.css';
import '../../styles/FactionRosterExperience.css';
import GMMatchManager from './GMMatchManager';
import MainEventerTimingFields from './MainEventerTimingFields';
import ManageRosterPageComp from './ManageRosterPageComp';
import ManageRosterPageIndy from './ManageRosterPageIndy';
import WrestlerAvailabilityList from './WrestlerAvailabilityList';
import ImageUploader from '../ImageUploader';
import FormInfoTip from '../ui/FormInfoTip';
import {
  buildChangeDescription,
  formatAdminBoolean,
  formatAdminDate,
  recordAdminUpdate
} from '../../utils/adminUpdates';
import { rosterFormInfo } from '../../utils/rosterFormInfo';
import {
  MAIN_EVENTER_COOLDOWN_DAYS,
  MAIN_EVENTER_DURATION_DAYS,
  clampMainEventerTimingDays,
} from '../../utils/mainEventerCooldown';

const MIN_PARTICIPANTS = 3;
const MAX_PARTICIPANTS = 20;
const MIN_WRESTLERS = 6;
const MAX_WRESTLERS = 20;

const initialRosterForm = {
  name: '',
  maxWrestlers: 6,
  maxSwap: 8,
  maxCredits: 300,
  dataInizioPunteggio: '',
  imageUrl: '',
  description: '',
  minMaleWrestlers: 0,
  minFemaleWrestlers: 0,
  onlyMainRoster: false,
  onlyNXT: false,
  hasBackstage: false,
  backstageCount: 0,
  hasMainEventer: true,
  mainEventerDurationDays: MAIN_EVENTER_DURATION_DAYS,
  mainEventerCooldownDays: MAIN_EVENTER_COOLDOWN_DAYS,
  isGM: false,
};

const isParticipantId = (value) => typeof value === 'string' && !value.includes('@');

const formatDateForDisplay = (date) => {
  if (!date) return '';

  let dateObj;
  if (date?.seconds) {
    dateObj = new Date(date.seconds * 1000);
  } else if (typeof date?.toDate === 'function') {
    dateObj = date.toDate();
  } else if (date instanceof Date) {
    dateObj = date;
  } else {
    dateObj = new Date(date);
  }

  if (Number.isNaN(dateObj.getTime())) return '';

  const day = String(dateObj.getDate()).padStart(2, '0');
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const year = dateObj.getFullYear();

  return `${day}/${month}/${year}`;
};

const formatDateForInput = (date) => {
  if (!date) return '';

  let dateObj;
  if (date?.seconds) {
    dateObj = new Date(date.seconds * 1000);
  } else if (typeof date?.toDate === 'function') {
    dateObj = date.toDate();
  } else if (date instanceof Date) {
    dateObj = date;
  } else {
    dateObj = new Date(date);
  }

  if (Number.isNaN(dateObj.getTime())) return '';
  return dateObj.toISOString().split('T')[0];
};

const ManageRosterPage = () => {
  const { rosterId } = useParams();
  const navigate = useNavigate();

  const [roster, setRoster] = useState(null);
  const [factions, setFactions] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [wrestlers, setWrestlers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [authLoading, setAuthLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [isAuthorized, setIsAuthorized] = useState(false);

  const [showEditRoster, setShowEditRoster] = useState(false);
  const [showEditParticipants, setShowEditParticipants] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmed, setDeleteConfirmed] = useState(false);

  const [editRosterData, setEditRosterData] = useState(initialRosterForm);
  const [editParticipants, setEditParticipants] = useState([]);
  const [newParticipantEmail, setNewParticipantEmail] = useState('');
  const [searchParticipant, setSearchParticipant] = useState('');
  const [rosterMessage, setRosterMessage] = useState('');
  const [participantsMessage, setParticipantsMessage] = useState('');
  const [savingRoster, setSavingRoster] = useState(false);
  const [savingParticipants, setSavingParticipants] = useState(false);
  const [deletingRoster, setDeletingRoster] = useState(false);
  const [activeSection, setActiveSection] = useState('info');

  const selectedParticipants = useMemo(
    () =>
      participants
        .filter((user) => editParticipants.includes(user.id))
        .sort((a, b) =>
          (a.username || a.email || '').localeCompare(b.username || b.email || '', 'it')
        ),
    [participants, editParticipants]
  );

  const resolveParticipantsToUsers = useCallback(async (participantIds = []) => {
    if (!participantIds.length) return [];

    const resolvedUsers = [];
    for (const participantRef of participantIds) {
      if (isParticipantId(participantRef)) {
        const userDoc = await getDoc(doc(db, 'users', participantRef));
        if (userDoc.exists()) {
          resolvedUsers.push({ id: userDoc.id, ...userDoc.data() });
        }
        continue;
      }

      const userQuery = query(
        collection(db, 'users'),
        where('email', '==', participantRef.trim().toLowerCase())
      );
      const userSnapshot = await getDocs(userQuery);
      userSnapshot.forEach((userDoc) => {
        resolvedUsers.push({ id: userDoc.id, ...userDoc.data() });
      });
    }

    const uniqueUsers = new Map();
    resolvedUsers.forEach((user) => uniqueUsers.set(user.id, user));
    return Array.from(uniqueUsers.values());
  }, []);

  const loadRosterAndFactions = useCallback(async () => {
    const rosterDoc = await getDoc(doc(db, 'rosters', rosterId));
    if (!rosterDoc.exists()) {
      throw new Error('Roster non trovato');
    }

    const rosterData = { id: rosterDoc.id, ...rosterDoc.data() };
    const factionsQuery = query(collection(db, 'factions'), where('rosterId', '==', rosterDoc.id));
    const factionsSnapshot = await getDocs(factionsQuery);
    const factionsData = factionsSnapshot.docs.map((factionDoc) => ({
      id: factionDoc.id,
      ...factionDoc.data(),
    }));

    return { rosterData, factionsData };
  }, [rosterId]);

  const loadData = useCallback(async () => {
    setLoading(true);

    try {
      const [{ rosterData, factionsData }, wrestlersSnapshot, usersSnapshot] = await Promise.all([
        loadRosterAndFactions(),
        getDocs(collection(db, 'wrestlers')),
        getDocs(collection(db, 'users')),
      ]);

      const participantsData = await resolveParticipantsToUsers(rosterData.participantIds || []);

      setRoster(rosterData);
      setFactions(factionsData);
      setParticipants(participantsData);
      setWrestlers(
        wrestlersSnapshot.docs
          .map((wrestlerDoc) => ({ id: wrestlerDoc.id, ...wrestlerDoc.data() }))
          .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'it'))
      );
      setAllUsers(usersSnapshot.docs.map((userDoc) => ({ id: userDoc.id, ...userDoc.data() })));
    } catch (error) {
      console.error('Errore caricamento dati roster:', error);
      setRoster(null);
    } finally {
      setLoading(false);
    }
  }, [loadRosterAndFactions, resolveParticipantsToUsers]);

  const reloadFactions = useCallback(async () => {
    try {
      const { rosterData, factionsData } = await loadRosterAndFactions();
      const participantsData = await resolveParticipantsToUsers(rosterData.participantIds || []);
      setRoster(rosterData);
      setFactions(factionsData);
      setParticipants(participantsData);
    } catch (error) {
      console.error('Errore ricaricamento roster:', error);
    }
  }, [loadRosterAndFactions, resolveParticipantsToUsers]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      setAuthLoading(false);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!roster || !currentUser) {
      setIsAuthorized(false);
      return;
    }

    setIsAuthorized(roster.ownerUid === currentUser.uid);
  }, [currentUser, roster]);

  const openRosterEditor = () => {
    const maxWrestlers = Number(roster?.maxWrestlers) || MIN_WRESTLERS;
    setEditRosterData({
      name: roster?.name || '',
      maxWrestlers,
      maxSwap: Number(roster?.maxSwap ?? 8),
      maxCredits: Number(roster?.maxCredits ?? maxWrestlers * 50),
      dataInizioPunteggio: formatDateForInput(roster?.dataInizioPunteggio),
      imageUrl: roster?.imageUrl || '',
      description: roster?.description || '',
      minMaleWrestlers: Number(roster?.minMaleWrestlers || 0),
      minFemaleWrestlers: Number(roster?.minFemaleWrestlers || 0),
      onlyMainRoster: Boolean(roster?.onlyMainRoster),
      onlyNXT: Boolean(roster?.onlyNXT),
      hasBackstage: Boolean(roster?.hasBackstage),
      backstageCount: Number(roster?.backstageCount || 0),
      hasMainEventer: roster?.hasMainEventer !== false,
      mainEventerDurationDays: clampMainEventerTimingDays(
        roster?.mainEventerDurationDays,
        MAIN_EVENTER_DURATION_DAYS
      ),
      mainEventerCooldownDays: clampMainEventerTimingDays(
        roster?.mainEventerCooldownDays,
        MAIN_EVENTER_COOLDOWN_DAYS
      ),
      isGM: Boolean(roster?.isGM),
    });
    setRosterMessage('');
    setShowEditRoster(true);
  };

  const openParticipantsEditor = () => {
    const participantIds = Array.isArray(roster?.participantIds) ? roster.participantIds : [];
    const normalizedIds = participantIds.filter(isParticipantId);
    const ownerId = roster?.ownerUid;

    setEditParticipants(Array.from(new Set([ownerId, ...normalizedIds].filter(Boolean))));
    setNewParticipantEmail('');
    setSearchParticipant('');
    setParticipantsMessage('');
    setShowEditParticipants(true);
  };

  const handleRosterField = (key, value) => {
    setEditRosterData((prev) => {
      if (key === 'onlyMainRoster' && value === true) {
        return { ...prev, onlyMainRoster: true, onlyNXT: false };
      }

      if (key === 'onlyNXT' && value === true) {
        return { ...prev, onlyNXT: true, onlyMainRoster: false };
      }

      return { ...prev, [key]: value };
    });
  };

  const resetRosterForm = () => {
    if (!roster) return;
    const maxWrestlers = Number(roster.maxWrestlers) || MIN_WRESTLERS;
    setEditRosterData({
      name: roster.name || '',
      maxWrestlers,
      maxSwap: Number(roster.maxSwap ?? 8),
      maxCredits: Number(roster.maxCredits ?? maxWrestlers * 50),
      dataInizioPunteggio: formatDateForInput(roster.dataInizioPunteggio),
      imageUrl: roster.imageUrl || '',
      description: roster.description || '',
      minMaleWrestlers: Number(roster.minMaleWrestlers || 0),
      minFemaleWrestlers: Number(roster.minFemaleWrestlers || 0),
      onlyMainRoster: Boolean(roster.onlyMainRoster),
      onlyNXT: Boolean(roster.onlyNXT),
      hasBackstage: Boolean(roster.hasBackstage),
      backstageCount: Number(roster.backstageCount || 0),
      hasMainEventer: roster.hasMainEventer !== false,
      mainEventerDurationDays: clampMainEventerTimingDays(
        roster.mainEventerDurationDays,
        MAIN_EVENTER_DURATION_DAYS
      ),
      mainEventerCooldownDays: clampMainEventerTimingDays(
        roster.mainEventerCooldownDays,
        MAIN_EVENTER_COOLDOWN_DAYS
      ),
      isGM: Boolean(roster.isGM),
    });
  };

  const handleSaveRoster = async (event) => {
    event.preventDefault();
    if (!roster) return;

    setSavingRoster(true);
    setRosterMessage('');

    try {
      if (editRosterData.onlyMainRoster && editRosterData.onlyNXT) {
        throw new Error('Non puoi attivare contemporaneamente Solo Main Roster e Solo NXT');
      }

      const payload = {
        name: editRosterData.name.trim(),
        maxWrestlers: Math.max(
          MIN_WRESTLERS,
          Math.min(MAX_WRESTLERS, Number(editRosterData.maxWrestlers) || MIN_WRESTLERS)
        ),
        maxSwap: Math.max(0, Number(editRosterData.maxSwap) || 0),
        maxCredits: Math.max(1, Number(editRosterData.maxCredits) || 1),
        imageUrl: editRosterData.imageUrl || '',
        description: editRosterData.description.trim(),
        minMaleWrestlers: Math.max(0, Number(editRosterData.minMaleWrestlers) || 0),
        minFemaleWrestlers: Math.max(0, Number(editRosterData.minFemaleWrestlers) || 0),
        onlyMainRoster: Boolean(editRosterData.onlyMainRoster),
        onlyNXT: Boolean(editRosterData.onlyNXT),
        hasBackstage: Boolean(editRosterData.hasBackstage),
        backstageCount: editRosterData.hasBackstage ? Math.max(1, Number(editRosterData.backstageCount) || 1) : 0,
        hasMainEventer: Boolean(editRosterData.hasMainEventer),
        mainEventerDurationDays: clampMainEventerTimingDays(
          editRosterData.mainEventerDurationDays,
          MAIN_EVENTER_DURATION_DAYS
        ),
        mainEventerCooldownDays: clampMainEventerTimingDays(
          editRosterData.mainEventerCooldownDays,
          MAIN_EVENTER_COOLDOWN_DAYS
        ),
        isGM: Boolean(editRosterData.isGM),
        dataInizioPunteggio: editRosterData.dataInizioPunteggio
          ? new Date(`${editRosterData.dataInizioPunteggio}T00:00:00`)
          : null,
      };

      if (!payload.name || payload.name.length < 3) {
        throw new Error('Il nome roster deve avere almeno 3 caratteri');
      }

      if (!payload.dataInizioPunteggio || Number.isNaN(payload.dataInizioPunteggio.getTime())) {
        throw new Error('Inserisci una data valida per l’inizio punteggio');
      }
      if (payload.minMaleWrestlers + payload.minFemaleWrestlers > payload.maxWrestlers) {
        throw new Error('La somma dei minimi uomo e donna non puo superare i wrestler massimi');
      }
      if (payload.hasBackstage) {
        const maxBackstage = Math.max(payload.maxWrestlers - (payload.hasMainEventer ? 1 : 0), 0);
        if (payload.backstageCount < 1 || payload.backstageCount > maxBackstage) {
          throw new Error(`Il backstage deve essere compreso tra 1 e ${maxBackstage}`);
        }
      }

      await updateDoc(doc(db, 'rosters', roster.id), {
        ...payload,
        updatedAt: new Date(),
      });
      await recordAdminUpdate({
        type: 'Roster',
        action: 'Modifica',
        title: payload.name,
        description: buildChangeDescription('Impostazioni roster modificate', roster, payload, [
          { key: 'name', label: 'nome' },
          { key: 'imageUrl', label: 'immagine', renderChange: () => 'immagine aggiornata' },
          { key: 'description', label: 'descrizione' },
          { key: 'dataInizioPunteggio', label: 'data inizio punteggio', format: formatAdminDate },
          { key: 'maxWrestlers', label: 'max wrestler' },
          { key: 'maxSwap', label: 'max swap' },
          { key: 'maxCredits', label: 'max crediti' },
          { key: 'minMaleWrestlers', label: 'min uomini' },
          { key: 'minFemaleWrestlers', label: 'min donne' },
          { key: 'onlyMainRoster', label: 'solo main roster', format: formatAdminBoolean },
          { key: 'onlyNXT', label: 'solo NXT', format: formatAdminBoolean },
          { key: 'hasBackstage', label: 'backstage', format: formatAdminBoolean },
          { key: 'backstageCount', label: 'numero backstage' },
          { key: 'hasMainEventer', label: 'main eventer', format: formatAdminBoolean },
          { key: 'mainEventerDurationDays', label: 'durata main eventer (giorni)' },
          { key: 'mainEventerCooldownDays', label: 'cooldown main eventer (giorni)' },
          { key: 'isGM', label: 'GM', format: formatAdminBoolean }
        ]),
        entityType: 'rosters',
        entityId: roster.id
      });

      setRoster((prev) => (prev ? { ...prev, ...payload } : prev));
      setRosterMessage('Roster aggiornato con successo.');
      setShowEditRoster(false);
    } catch (error) {
      setRosterMessage(`Errore salvataggio roster: ${error.message}`);
    } finally {
      setSavingRoster(false);
    }
  };

  const addParticipantByEmail = async () => {
    const normalizedEmail = newParticipantEmail.trim().toLowerCase();

    if (!normalizedEmail) {
      setParticipantsMessage('Inserisci un indirizzo email valido.');
      return;
    }

    if (editParticipants.length >= MAX_PARTICIPANTS) {
      setParticipantsMessage(`Hai raggiunto il massimo di ${MAX_PARTICIPANTS} partecipanti.`);
      return;
    }

    try {
      const userQuery = query(collection(db, 'users'), where('email', '==', normalizedEmail));
      const userSnapshot = await getDocs(userQuery);

      if (userSnapshot.empty) {
        setParticipantsMessage('Utente non trovato.');
        return;
      }

      const userDoc = userSnapshot.docs[0];
      if (editParticipants.includes(userDoc.id)) {
        setParticipantsMessage('Questo utente è già presente nel roster.');
        return;
      }

      setEditParticipants((prev) => [...prev, userDoc.id]);
      setParticipants((prev) => {
        if (prev.some((user) => user.id === userDoc.id)) return prev;
        return [...prev, { id: userDoc.id, ...userDoc.data() }];
      });
      setNewParticipantEmail('');
      setParticipantsMessage(`${userDoc.data().username || userDoc.data().email} aggiunto alla lista.`);
    } catch (error) {
      setParticipantsMessage(`Errore aggiunta partecipante: ${error.message}`);
    }
  };

  const toggleParticipant = (userId) => {
    const isOwner = userId === roster?.ownerUid;
    if (isOwner) {
      setParticipantsMessage('Il proprietario del roster deve restare tra i partecipanti.');
      return;
    }

    const isSelected = editParticipants.includes(userId);
    if (isSelected && editParticipants.length <= MIN_PARTICIPANTS) {
      setParticipantsMessage(`Devono rimanere almeno ${MIN_PARTICIPANTS} partecipanti.`);
      return;
    }

    setEditParticipants((prev) =>
      isSelected ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
    setParticipantsMessage('');
  };

  const handleSaveParticipants = async (event) => {
    event.preventDefault();
    if (!roster) return;

    const uniqueParticipants = Array.from(new Set(editParticipants.filter(Boolean)));
    if (!uniqueParticipants.includes(roster.ownerUid)) {
      uniqueParticipants.unshift(roster.ownerUid);
    }

    if (uniqueParticipants.length < MIN_PARTICIPANTS || uniqueParticipants.length > MAX_PARTICIPANTS) {
      setParticipantsMessage(
        `Il roster deve avere tra ${MIN_PARTICIPANTS} e ${MAX_PARTICIPANTS} partecipanti.`
      );
      return;
    }

    setSavingParticipants(true);
    setParticipantsMessage('');

    try {
      const previousParticipantIds = (roster.participantIds || []).filter(isParticipantId);
      const addedIds = uniqueParticipants.filter((userId) => !previousParticipantIds.includes(userId));
      const removedIds = previousParticipantIds.filter((userId) => !uniqueParticipants.includes(userId));

      await updateDoc(doc(db, 'rosters', roster.id), {
        participantIds: uniqueParticipants,
        updatedAt: new Date(),
      });

      await Promise.all([
        ...addedIds.map((userId) =>
          updateDoc(doc(db, 'users', userId), { rosterIds: arrayUnion(roster.id) })
        ),
        ...removedIds.map((userId) =>
          updateDoc(doc(db, 'users', userId), { rosterIds: arrayRemove(roster.id) })
        ),
      ]);

      const participantsData = await resolveParticipantsToUsers(uniqueParticipants);
      await recordAdminUpdate({
        type: 'Partecipazioni',
        action: 'Modifica',
        title: roster.name || roster.id,
        description: `Partecipanti roster aggiornati: +${addedIds.length}, -${removedIds.length}`,
        entityType: 'rosters',
        entityId: roster.id,
        metadata: {
          addedIds,
          removedIds
        }
      });
      setParticipants(participantsData);
      setRoster((prev) => (prev ? { ...prev, participantIds: uniqueParticipants } : prev));
      setParticipantsMessage('Partecipanti aggiornati con successo.');
      setShowEditParticipants(false);
    } catch (error) {
      setParticipantsMessage(`Errore salvataggio partecipanti: ${error.message}`);
    } finally {
      setSavingParticipants(false);
    }
  };

  const handleDeleteRoster = async () => {
    if (!roster) return;

    setDeletingRoster(true);
    setRosterMessage('');

    try {
      const [factionsSnapshot, matchesSnapshot] = await Promise.all([
        getDocs(query(collection(db, 'factions'), where('rosterId', '==', roster.id))),
        getDocs(collection(db, 'rosters', roster.id, 'matches')),
      ]);

      await Promise.all([
        ...factionsSnapshot.docs.map((factionDoc) => deleteDoc(factionDoc.ref)),
        ...matchesSnapshot.docs.map((matchDoc) => deleteDoc(matchDoc.ref)),
      ]);

      const participantIds = (roster.participantIds || []).filter(isParticipantId);
      await Promise.all([
        ...participantIds.map((participantId) =>
          updateDoc(doc(db, 'users', participantId), { rosterIds: arrayRemove(roster.id) })
        ),
        updateDoc(doc(db, 'users', roster.ownerUid), {
          ownerOfRosters: arrayRemove(roster.id),
        }),
      ]);

      await deleteDoc(doc(db, 'rosters', roster.id));
      navigate('/profile');
    } catch (error) {
      console.error('Errore eliminazione roster:', error);
      setRosterMessage(`Errore durante l'eliminazione del roster: ${error.message}`);
    } finally {
      setDeletingRoster(false);
      setShowDeleteModal(false);
      setDeleteConfirmed(false);
    }
  };

  const inviteLink = roster ? `${window.location.origin}/invite/${roster.id}` : '';

  const handleShareInvite = async (event) => {
    const button = event.currentTarget;
    const originalText = button.innerHTML;
    button.innerHTML = '<i class="bi bi-check-lg me-2"></i>Condiviso!';
    button.disabled = true;

    try {
      if (navigator.share) {
        await navigator.share({
          title: 'Invito roster',
          text: 'Unisciti al mio roster!',
          url: inviteLink,
        });
      } else {
        await navigator.clipboard.writeText(inviteLink);
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        button.innerHTML = '<i class="bi bi-exclamation-triangle me-2"></i>Errore';
        setTimeout(() => {
          button.innerHTML = originalText;
          button.disabled = false;
        }, 2000);
        return;
      }
    }

    setTimeout(() => {
      button.innerHTML = originalText;
      button.disabled = false;
    }, 2000);
  };

  if (loading) {
    return (
      <div className="l">
        <div className="manage-roster-status">
          <div className="spinner-border text-light" role="status">
            <span className="visually-hidden">Caricamento...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!roster) {
    return (
      <div className="">
        <div className="manage-roster-status manage-roster-status--warning">
          <h4 className="mb-2">Roster non trovato</h4>
          <p className="mb-3">Il roster richiesto non è disponibile o è stato rimosso.</p>
          <button className="btn btn-outline-light" onClick={() => navigate(-1)}>
            Torna indietro
          </button>
        </div>
      </div>
    );
  }

  if (authLoading) {
    return (
      <div className="">
        <div className="manage-roster-status">
          <div className="spinner-border text-light" role="status">
            <span className="visually-hidden">Caricamento...</span>
          </div>
          <p className="mb-0">Verifica autorizzazione...</p>
        </div>
      </div>
    );
  }

  if (!isAuthorized) {
    return (
      <div className="">
        <div className="manage-roster-status manage-roster-status--danger">
          <h4 className="mb-2">Accesso negato</h4>
          <p className="mb-3">Solo il proprietario del roster può gestire impostazioni, fazioni e partecipanti.</p>
          <button className="btn btn-outline-light" onClick={() => navigate(-1)}>
            Torna indietro
          </button>
        </div>
      </div>
    );
  }

  const navigationItems = [
    { id: 'info', label: 'Info', icon: 'bi-info-circle' },
    { id: 'participants', label: 'Partecipanti e fazioni', icon: 'bi-people' },
    { id: 'available', label: 'Wrestler liberi', icon: 'bi-stars' },
    { id: 'invite', label: 'Link di invito', icon: 'bi-send' },
    ...(roster?.isGM ? [{ id: 'matches', label: 'Gestione match', icon: 'bi-controller' }] : []),
  ];

  return (
    <div className="frx-page min-vh-100 py-4">
      <div className="container manage-roster-page">
        <div className="manage-roster-workspace">
          <aside className="manage-roster-sidebar">
            <div className="manage-roster-sidebar__head">
              <span className="manage-roster-eyebrow">Navigazione</span>
            </div>

            <nav className="manage-roster-sidebar__nav" aria-label="Sezioni roster">
              {navigationItems.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className={`manage-roster-nav-btn ${activeSection === item.id ? 'is-active' : ''}`}
                  onClick={() => setActiveSection(item.id)}
                  aria-current={activeSection === item.id ? 'page' : undefined}
                  aria-pressed={activeSection === item.id}
                >
                  <span className="manage-roster-nav-btn__icon">
                    <i className={`bi ${item.icon}`}></i>
                  </span>
                  <span className="manage-roster-nav-btn__label">{item.label}</span>
                  {typeof item.badge === 'number' && (
                    <span className={`manage-roster-nav-btn__badge ${item.badge > 0 ? 'has-items' : ''}`}>
                      {item.badge}
                    </span>
                  )}
                </button>
              ))}
            </nav>
          </aside>

          <div className="manage-roster-content">
            {activeSection === 'info' && (
              <>
                <section className="manage-roster-section manage-roster-section--hero">
                  <div className="manage-roster-section__header">
                    <div>
                      <span className="manage-roster-section__eyebrow">Info roster</span>
                      <h2>{roster.name}</h2>
                    </div>
                  </div>

                  <div className="d-flex flex-nowrap gap-2 justify-content-center justify-content-sm-start overflow-auto">
                    <button className="btn btn-sm btn-outline-brand-secondary px-3 px-sm-4" onClick={openRosterEditor}>
                      <i className="bi bi-sliders me-1 me-sm-2" />
                      Impostazioni
                    </button>

                    <button className="btn btn-sm btn-outline-brand-primary-light px-3 px-sm-4" onClick={openParticipantsEditor}>
                      <i className="bi bi-people me-1 me-sm-2" />
                      Partecipanti
                    </button>

                    <button className="btn btn-sm btn-brand-accent px-2 px-sm-3" onClick={() => setShowDeleteModal(true)}>
                      <i className="bi bi-trash" />
                    </button>
                  </div>

                  <div className="manage-roster-stats">
                    <div className="manage-roster-stat">
                      <span className="manage-roster-stat__label">Partecipanti</span>
                      <strong>{participants.length}</strong>
                    </div>
                    <div className="manage-roster-stat">
                      <span className="manage-roster-stat__label">Fazioni</span>
                      <strong>{factions.length}</strong>
                      <span className="text-secondary m-2">|</span>
                      <span className="manage-roster-stat__label">Scambi</span>
                      <strong>{roster.maxSwap ?? 8}</strong>
                    </div>
                    <div className="manage-roster-stat">
                      <span className="manage-roster-stat__label">Wrestler</span>
                      <strong>{roster.maxWrestlers}</strong>
                      <span className="text-secondary m-2">|</span>
                      <span className="manage-roster-stat__label">Crediti</span>
                      <strong>{roster.maxCredits}</strong>
                    </div>
                    <div className="manage-roster-stat">
                      <span className="manage-roster-stat__label">Modalita</span>
                      <strong>{roster.gameType || 'Competitivo'}</strong>
                    </div>
                    <div className="manage-roster-stat">
                      <span className="manage-roster-stat__label">Requisiti fazione</span>
                      <div className="frx-pill-row mt-1">
                        <span className="badge bg-male p-1">
                          <i className="bi bi-gender-male me-1"></i> {roster.minMaleWrestlers || 0}
                        </span>
                        <span className="badge bg-female p-1">
                          <i className="bi bi-gender-female me-1"></i> {roster.minFemaleWrestlers || 0}
                        </span>
                        {roster.onlyMainRoster && <span className="badge bg-brand-primary p-1">Solo Main Roster</span>}
                        {roster.onlyNXT && <span className="badge bg-brand-primary p-1">Solo NXT</span>}
                      </div>
                    </div>
                    <div className="manage-roster-stat">
                      <p className="manage-roster-stat__label">Inizio punteggio</p>
                      <h5 className="text-light mb-0">{formatDateForDisplay(roster.dataInizioPunteggio) || 'Non impostato'}</h5>
                    </div>
                  </div>
                </section>

                {(rosterMessage || participantsMessage) && (
                  <div className="manage-roster-feedback-stack">
                    {rosterMessage && (
                      <div
                        className={`alert ${rosterMessage.toLowerCase().includes('errore') ? 'alert-danger' : 'alert-success'} mb-0`}
                      >
                        {rosterMessage}
                      </div>
                    )}
                    {participantsMessage && (
                      <div
                        className={`alert ${participantsMessage.toLowerCase().includes('errore') ? 'alert-danger' : 'alert-success'} mb-0`}
                      >
                        {participantsMessage}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            {activeSection === 'participants' && (
              roster.gameType === 'Competitivo' ? (
                <ManageRosterPageComp
                  roster={roster}
                  factions={factions}
                  participants={participants}
                  wrestlers={wrestlers}
                  allUsers={allUsers}
                  onReloadFactions={reloadFactions}
                  activeSection={activeSection}
                />
              ) : (
                <ManageRosterPageIndy
                  roster={roster}
                  factions={factions}
                  participants={participants}
                  wrestlers={wrestlers}
                  activeSection={activeSection}
                />
              )
            )}

            {activeSection === 'available' && (
              <WrestlerAvailabilityList
                wrestlers={wrestlers}
                factions={factions}
                onlyMainRoster={Boolean(roster.onlyMainRoster)}
                onlyNXT={Boolean(roster.onlyNXT)}
              />
            )}

            {activeSection === 'invite' && (
              <section className="manage-roster-section">
                <div className="manage-roster-section__header">
                  <div>
                    <span className="manage-roster-section__eyebrow">Invito</span>
                    <h2>Link di invito</h2>
                  </div>
                </div>

                <div className="manage-roster-invite">
                  <div>
                    <strong>{inviteLink}</strong>
                  </div>
                  <button className="btn btn-outline-brand-secondary" onClick={handleShareInvite}>
                    <i className="bi bi-share me-2" />Condividi link
                  </button>
                </div>
              </section>
            )}

            {activeSection === 'matches' && roster?.isGM && (
              <GMMatchManager
                rosterId={roster.id}
                roster={roster}
                factions={factions}
                participants={participants}
                isGM={roster.isGM}
              />
            )}
          </div>
        </div>


        {showEditRoster && (
          <div className="manage-roster-modal-backdrop">
            <div className="manage-roster-modal manage-roster-modal--form manage-roster-editor-modal">
              <div className="manage-roster-modal__header">
                <div>
                  <h2 className="manage-roster-panel__title">Modifica roster</h2>
                </div>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setShowEditRoster(false)}
                />
              </div>

              <form className="manage-roster-modal__body manage-roster-editor-form" onSubmit={handleSaveRoster}>
                <div className="form-info-banner mb-4">
                  <i className="bi bi-info-circle-fill form-info-banner__icon"></i>
                  <div>
                    <strong className="form-info-banner__title">Guida rapida al roster</strong>
                    <p className="form-info-banner__text">
                      Qui puoi aggiornare regole, budget, filtri e ruoli speciali con la stessa logica del form di creazione.
                    </p>
                  </div>
                </div>

                <div className="mb-4">
                  <div className="form-info-row--wrap mb-2">
                    <label className="form-label fw-semibold mb-0 d-flex align-items-center gap-2 text-brand-secondary">
                      <i className="bi bi-tag"></i>
                      <span className="text-light">Nome roster</span>
                    </label>
                    <FormInfoTip title={rosterFormInfo.name.title}>
                      {rosterFormInfo.name.body}
                    </FormInfoTip>
                  </div>
                  <input
                    type="text"
                    className="form-control py-2"
                    value={editRosterData.name}
                    onChange={(event) => handleRosterField('name', event.target.value)}
                    minLength={3}
                    required
                    style={{
                      background: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      color: 'white'
                    }}
                  />
                </div>

                <div className="bg-dark rounded-3 p-3 mb-4">
                  <div className="form-info-row--between mb-3 text-brand-secondary">
                    <div className="d-flex align-items-center gap-2">
                      <i className="bi bi-sliders2"></i>
                      <span className="fw-semibold text-light">Wrestler e crediti</span>
                    </div>
                    <FormInfoTip title={rosterFormInfo.slotsAndCredits.title}>
                      {rosterFormInfo.slotsAndCredits.body}
                    </FormInfoTip>
                  </div>

                  <div className="row g-3">
                    <div className="col-6">
                      <label className="form-label small text-brand-muted mb-1">Wrestler per fazione</label>
                      <div className="d-flex align-items-center gap-2">
                        <i className="bi bi-people-fill text-brand-secondary"></i>
                        <input
                          type="range"
                          className="form-range flex-grow-1"
                          min={MIN_WRESTLERS}
                          max={MAX_WRESTLERS}
                          step={1}
                          value={editRosterData.maxWrestlers}
                          onChange={(event) =>
                            handleRosterField(
                              'maxWrestlers',
                              Math.max(
                                MIN_WRESTLERS,
                                Math.min(MAX_WRESTLERS, Number(event.target.value) || MIN_WRESTLERS)
                              )
                            )
                          }
                        />
                        <span className="badge bg-brand-secondary text-dark rounded-pill">
                          {editRosterData.maxWrestlers}
                        </span>
                      </div>
                    </div>

                    <div className="col-6">
                      <label className="form-label small text-brand-muted mb-1">Scambi max</label>
                      <div className="d-flex align-items-center gap-2">
                        <i className="bi bi-arrow-left-right text-brand-secondary"></i>
                        <input
                          type="number"
                          className="form-control form-control-sm"
                          min={0}
                          value={editRosterData.maxSwap}
                          onChange={(event) =>
                            handleRosterField('maxSwap', Math.max(0, Number(event.target.value) || 0))
                          }
                          style={{
                            background: 'rgba(255,255,255,0.05)',
                            border: 'none',
                            color: 'white'
                          }}
                          required
                        />
                      </div>
                    </div>

                    <div className="col-12">
                      <label className="form-label small text-brand-muted mb-1">Crediti per giocatore</label>
                      <div className="d-flex align-items-center gap-2">
                        <i className="bi bi-currency-dollar text-brand-secondary"></i>
                        <input
                          type="number"
                          className="form-control form-control-sm"
                          min={1}
                          value={editRosterData.maxCredits}
                          onChange={(event) =>
                            handleRosterField('maxCredits', Math.max(1, Number(event.target.value) || 1))
                          }
                          style={{
                            background: 'rgba(255,255,255,0.05)',
                            border: 'none',
                            color: 'white'
                          }}
                          required
                        />
                      </div>
                      <div className="manage-roster-inline-note mt-2 text-brand-muted">
                        <small>Suggerito: {Number(editRosterData.maxWrestlers) * 50} crediti</small>
                        <button
                          type="button"
                          className="btn btn-outline-brand-secondary btn-sm"
                          onClick={() => handleRosterField('maxCredits', Number(editRosterData.maxWrestlers) * 50)}
                        >
                          Usa suggerito
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="form-info-row mb-2">
                  <span className="small fw-semibold text-brand-secondary">Vincoli di composizione</span>
                  <FormInfoTip title={rosterFormInfo.composition.title}>
                    {rosterFormInfo.composition.body}
                  </FormInfoTip>
                </div>
                <div className="row g-3 mb-4">
                  <div className="col-6">
                    <label className="form-label small text-brand-muted mb-1">Min. Uomini</label>
                    <div className="d-flex align-items-center gap-2">
                      <i className="bi bi-gender-male" style={{ color: '#3498db' }}></i>
                      <input
                        type="range"
                        className="form-range flex-grow-1"
                        min={0}
                        max={Math.floor(Number(editRosterData.maxWrestlers) / 2)}
                        step={1}
                        value={editRosterData.minMaleWrestlers}
                        onChange={(event) =>
                          handleRosterField('minMaleWrestlers', Math.max(0, Number(event.target.value) || 0))
                        }
                      />
                      <span className="badge bg-brand-secondary text-dark rounded-pill">
                        {editRosterData.minMaleWrestlers}
                      </span>
                    </div>
                  </div>
                  <div className="col-6">
                    <label className="form-label small text-brand-muted mb-1">Min. Donne</label>
                    <div className="d-flex align-items-center gap-2">
                      <i className="bi bi-gender-female" style={{ color: '#e84393' }}></i>
                      <input
                        type="range"
                        className="form-range flex-grow-1"
                        min={0}
                        max={Math.floor(Number(editRosterData.maxWrestlers) / 2)}
                        step={1}
                        value={editRosterData.minFemaleWrestlers}
                        onChange={(event) =>
                          handleRosterField('minFemaleWrestlers', Math.max(0, Number(event.target.value) || 0))
                        }
                      />
                      <span className="badge bg-brand-secondary text-dark rounded-pill">
                        {editRosterData.minFemaleWrestlers}
                      </span>
                    </div>
                  </div>
                </div>

                {Number(editRosterData.minMaleWrestlers) + Number(editRosterData.minFemaleWrestlers) > Number(editRosterData.maxWrestlers) && (
                  <div
                    className="alert alert-warning py-2 mb-4 small"
                    style={{
                      background: 'rgba(243,156,18,0.1)',
                      border: 'none',
                      color: '#f39c12'
                    }}
                  >
                    <i className="bi bi-exclamation-triangle me-1"></i>
                    Somma minimi supera il max wrestler
                  </div>
                )}

                <div className="bg-dark rounded-3 p-3 mb-4">
                  <div className="form-info-row--between mb-3">
                    <span className="small fw-semibold text-light">Filtri e ruoli speciali</span>
                    <FormInfoTip title={rosterFormInfo.options.title}>
                      {rosterFormInfo.options.body}
                    </FormInfoTip>
                  </div>
                  <div className="row g-2">
                    <div className="col-6">
                      <div className="d-flex align-items-center justify-content-between">
                        <div className="form-info-row">
                          <label className="form-check-label small text-light" htmlFor="editOnlyMainRoster">
                            Main Roster
                          </label>
                          <FormInfoTip title={rosterFormInfo.onlyMainRoster.title}>
                            {rosterFormInfo.onlyMainRoster.body}
                          </FormInfoTip>
                        </div>
                        <div className="form-check form-switch m-0">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            role="switch"
                            id="editOnlyMainRoster"
                            checked={editRosterData.onlyMainRoster}
                            onChange={(event) => handleRosterField('onlyMainRoster', event.target.checked)}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="d-flex align-items-center justify-content-between">
                        <div className="form-info-row">
                          <label className="form-check-label small text-light" htmlFor="editOnlyNXT">
                            Solo NXT
                          </label>
                          <FormInfoTip title={rosterFormInfo.onlyNXT.title}>
                            {rosterFormInfo.onlyNXT.body}
                          </FormInfoTip>
                        </div>
                        <div className="form-check form-switch m-0">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            role="switch"
                            id="editOnlyNXT"
                            checked={editRosterData.onlyNXT}
                            onChange={(event) => handleRosterField('onlyNXT', event.target.checked)}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="d-flex align-items-center justify-content-between">
                        <div className="form-info-row">
                          <label className="form-check-label small text-light" htmlFor="editIsGM">
                            Modalita GM
                          </label>
                          <FormInfoTip title={rosterFormInfo.gmMode.title}>
                            {rosterFormInfo.gmMode.body}
                          </FormInfoTip>
                        </div>
                        <div className="form-check form-switch m-0">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            role="switch"
                            id="editIsGM"
                            checked={editRosterData.isGM}
                            onChange={(event) => handleRosterField('isGM', event.target.checked)}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="d-flex align-items-center justify-content-between">
                        <div className="form-info-row">
                          <label className="form-check-label small text-light" htmlFor="editHasBackstage">
                            Backstage
                          </label>
                          <FormInfoTip title={rosterFormInfo.backstage.title}>
                            {rosterFormInfo.backstage.body}
                          </FormInfoTip>
                        </div>
                        <div className="form-check form-switch m-0">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            role="switch"
                            id="editHasBackstage"
                            checked={editRosterData.hasBackstage}
                            onChange={(event) => handleRosterField('hasBackstage', event.target.checked)}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="d-flex align-items-center justify-content-between">
                        <div className="form-info-row">
                          <label className="form-check-label small text-light" htmlFor="editHasMainEventer">
                            Main Eventer
                          </label>
                          <FormInfoTip title={rosterFormInfo.mainEventer.title}>
                            {rosterFormInfo.mainEventer.body}
                          </FormInfoTip>
                        </div>
                        <div className="form-check form-switch m-0">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            role="switch"
                            id="editHasMainEventer"
                            checked={editRosterData.hasMainEventer}
                            onChange={(event) => handleRosterField('hasMainEventer', event.target.checked)}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <MainEventerTimingFields
                  enabled={editRosterData.hasMainEventer}
                  durationDays={editRosterData.mainEventerDurationDays}
                  cooldownDays={editRosterData.mainEventerCooldownDays}
                  onDurationChange={(value) =>
                    handleRosterField('mainEventerDurationDays', value)
                  }
                  onCooldownChange={(value) =>
                    handleRosterField('mainEventerCooldownDays', value)
                  }
                  idPrefix="manage-roster-main-eventer"
                />

                {editRosterData.hasBackstage && (
                  <div className="mb-4">
                    <div className="form-info-row mb-1">
                      <label className="form-label small text-brand-muted mb-0">Wrestler in Backstage</label>
                      <FormInfoTip title={rosterFormInfo.backstageCount.title}>
                        {rosterFormInfo.backstageCount.body}
                      </FormInfoTip>
                    </div>
                    <div className="d-flex align-items-center gap-3">
                      <i className="bi bi-door-closed text-brand-secondary"></i>
                      <input
                        type="range"
                        className="form-range flex-grow-1"
                        min={1}
                        max={Math.max(Number(editRosterData.maxWrestlers) - (editRosterData.hasMainEventer ? 1 : 0), 1)}
                        step={1}
                        value={editRosterData.backstageCount}
                        onChange={(event) =>
                          handleRosterField('backstageCount', Math.max(1, Number(event.target.value) || 1))
                        }
                      />
                      <span className="badge bg-brand-secondary text-dark rounded-pill">
                        {editRosterData.backstageCount}
                      </span>
                    </div>
                  </div>
                )}

                <div className="mb-4">
                  <div className="form-info-row--wrap mb-2">
                    <label className="form-label fw-semibold mb-0 d-flex align-items-center gap-2 text-brand-secondary">
                      <i className="bi bi-calendar-event"></i>
                      <span className="text-light">Data inizio punteggio</span>
                    </label>
                    <FormInfoTip title={rosterFormInfo.startDate.title}>
                      {rosterFormInfo.startDate.body}
                    </FormInfoTip>
                  </div>
                  <input
                    type="date"
                    className="form-control py-2"
                    value={editRosterData.dataInizioPunteggio}
                    onChange={(event) => handleRosterField('dataInizioPunteggio', event.target.value)}
                    style={{
                      background: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      color: 'white'
                    }}
                    required
                  />
                </div>

                <div className="mb-4">
                  <div className="form-info-row--wrap mb-2">
                    <label className="form-label fw-semibold text-white mb-0">Descrizione</label>
                    <FormInfoTip title={rosterFormInfo.overview.title}>
                      Una descrizione breve aiuta i giocatori a capire tono, regole o obiettivo di questo roster.
                    </FormInfoTip>
                  </div>
                  <textarea
                    className="form-control"
                    rows="2"
                    value={editRosterData.description}
                    onChange={(event) => handleRosterField('description', event.target.value)}
                    placeholder="Descrivi il roster..."
                    style={{
                      background: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      color: 'white',
                      fontSize: '0.85rem'
                    }}
                  />
                </div>

                <div className="bg-dark rounded-3 p-3 mb-4">
                  <div className="form-info-row--between mb-3 text-brand-secondary">
                    <div className="d-flex align-items-center gap-2">
                      <i className="bi bi-image"></i>
                      <span className="fw-semibold text-light">Immagine roster</span>
                    </div>
                    <FormInfoTip title={rosterFormInfo.image.title}>
                      {rosterFormInfo.image.body}
                    </FormInfoTip>
                  </div>
                  <ImageUploader
                    currentImageUrl={editRosterData.imageUrl}
                    onUploadSuccess={(url) => handleRosterField('imageUrl', url)}
                    storagePath="rosters"
                    buttonText={editRosterData.imageUrl ? 'Cambia immagine' : 'Carica immagine'}
                  />
                  {editRosterData.imageUrl && (
                    <button
                      type="button"
                      className="btn btn-outline-danger btn-sm"
                      onClick={() => handleRosterField('imageUrl', '')}
                    >
                      <i className="bi bi-x-circle me-1"></i>
                      Rimuovi immagine
                    </button>
                  )}
                </div>

                <div className="manage-roster-modal__footer roster-form-actions">
                  <button
                    type="button"
                    className="btn roster-form-btn roster-form-btn--secondary"
                    onClick={() => setShowEditRoster(false)}
                  >
                    Annulla
                  </button>
                  <div className="roster-form-actions__group">
                    <button
                      type="button"
                      className="btn roster-form-btn roster-form-btn--ghost"
                      onClick={resetRosterForm}
                    >
                      Ripristina
                    </button>
                    <button
                      type="submit"
                      className="btn btn-brand-secondary roster-form-btn roster-form-btn--primary"
                      disabled={savingRoster}
                    >
                      {savingRoster ? 'Salvataggio...' : 'Salva modifiche'}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        )}

        {showEditParticipants && (
          <div className="manage-roster-modal-backdrop">
            <div className="manage-roster-modal manage-roster-modal--wide manage-roster-modal--form">
              <div className="manage-roster-modal__header">
                <div>
                  <h2 className="manage-roster-panel__title">Gestisci partecipanti</h2>
                </div>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setShowEditParticipants(false)}
                />
              </div>

              <form className="manage-roster-modal__body" onSubmit={handleSaveParticipants}>
                {participantsMessage && (
                  <div className={`alert ${participantsMessage.toLowerCase().includes('errore') || participantsMessage.toLowerCase().includes('non trovato') || participantsMessage.toLowerCase().includes('deve') ? 'alert-danger' : 'alert-info'} frx-alert mt-4`}>
                    {participantsMessage}
                  </div>
                )}

                <div className="manage-roster-participants-layout">
                  <div className="manage-roster-participants-card frx-field-card">
                    <div className="manage-roster-card-head">
                      <div>
                        <h3>Partecipanti attuali</h3>
                      </div>
                      <input
                        type="search"
                        className="form-control"
                        placeholder="Cerca nome o email"
                        value={searchParticipant}
                        onChange={(event) => setSearchParticipant(event.target.value)}
                      />
                    </div>

                    <div className="manage-roster-list">
                      {selectedParticipants
                        .filter(
                          (user) =>
                            (user.username || '').toLowerCase().includes(searchParticipant.toLowerCase()) ||
                            (user.email || '').toLowerCase().includes(searchParticipant.toLowerCase())
                        )
                        .map((user) => {
                          const isOwner = user.id === roster.ownerUid;
                          return (
                            <div className="manage-roster-user text-light" key={user.id}>
                              <div>
                                <strong>{user.username || 'Utente senza nome'}</strong>
                                <small>{user.email}</small>
                              </div>
                              <div className="manage-roster-user__actions">
                                {isOwner && <span className="badge bg-brand-secondary text-dark">Admin</span>}
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => toggleParticipant(user.id)}
                                  disabled={isOwner || editParticipants.length <= MIN_PARTICIPANTS}
                                >
                                  <i className="bi bi-trash" />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      {selectedParticipants.filter(
                        (user) =>
                          (user.username || '').toLowerCase().includes(searchParticipant.toLowerCase()) ||
                          (user.email || '').toLowerCase().includes(searchParticipant.toLowerCase())
                      ).length === 0 && (
                          <div className="frx-empty">
                            <i className="bi bi-search"></i>
                            <p className="mb-0">Nessun partecipante corrisponde alla ricerca.</p>
                          </div>
                        )}
                    </div>
                  </div>

                  <div className="manage-roster-participants-card frx-field-card">
                    <div className="manage-roster-card-head">
                      <div>
                        <h3>Aggiungi partecipante</h3>
                      </div>
                    </div>

                    <div className="input-group manage-roster-email-add">
                      <input
                        type="email"
                        className="form-control"
                        placeholder="email@esempio.com"
                        value={newParticipantEmail}
                        onChange={(event) => setNewParticipantEmail(event.target.value)}
                        disabled={editParticipants.length >= MAX_PARTICIPANTS}
                      />
                      <button
                        type="button"
                        className="btn btn-brand-primary"
                        onClick={addParticipantByEmail}
                        disabled={editParticipants.length >= MAX_PARTICIPANTS}
                      >
                        Aggiungi
                      </button>
                    </div>
                  </div>
                </div>

                <div className="manage-roster-modal__footer roster-form-actions mt-2">
                  <button
                    type="button"
                    className="btn roster-form-btn roster-form-btn--secondary"
                    onClick={() => setShowEditParticipants(false)}
                  >
                    Annulla
                  </button>
                  <button
                    type="submit"
                    className="btn btn-brand-secondary roster-form-btn roster-form-btn--primary"
                    disabled={
                      savingParticipants ||
                      editParticipants.length < MIN_PARTICIPANTS ||
                      editParticipants.length > MAX_PARTICIPANTS
                    }
                  >
                    {savingParticipants ? 'Salvataggio...' : 'Salva partecipanti'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

      </div>

      {showDeleteModal && (
        <div className="manage-roster-modal-backdrop">
          <div className="manage-roster-modal">
            <div className="manage-roster-modal__header">
              <div>
                <h2 className="manage-roster-panel__title">Elimina roster</h2>
              </div>
              <button
                type="button"
                className="btn-close btn-close-white"
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteConfirmed(false);
                }}
              />
            </div>

            <div className="manage-roster-modal__body text-light">
              <p>
                Stai per eliminare <strong>{roster.name}</strong>, tutte le sue fazioni e gli eventuali match collegati.
              </p>
              <label className="manage-roster-confirm">
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={deleteConfirmed}
                  onChange={(event) => setDeleteConfirmed(event.target.checked)}
                />
                <span>Confermo di voler eliminare permanentemente questo roster</span>
              </label>
            </div>

            <div className="manage-roster-modal__footer roster-form-actions">
              <button
                type="button"
                className="btn roster-form-btn roster-form-btn--secondary"
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteConfirmed(false);
                }}
              >
                Annulla
              </button>
              <button
                type="button"
                className="btn roster-form-btn roster-form-btn--danger"
                onClick={handleDeleteRoster}
                disabled={!deleteConfirmed || deletingRoster}
              >
                {deletingRoster ? 'Eliminazione...' : 'Elimina roster'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageRosterPage;
